<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import * as dataGrid from '@/composables/data-grid'
import { useDocumentIdentifiers } from '@/composables/document'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useEntityLock } from '@/services/entity-lock.service'
import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxSplitter, DxItem } from 'devextreme-vue/splitter'
import { DxTabPanel, DxItem as DxTabItem } from 'devextreme-vue/tab-panel'
import { useColumns, useDialogTitle } from '@/composables/registry/warehouse-document-grid'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import SubjectDropDownBox from '@/components/shared/SubjectDropDownBox.vue'
import WarehouseDocumentRowGrid from '@/components/registry/WarehouseDocumentRowGrid.vue'
import WarehouseReceiptSummary from '@/components/registry/WarehouseReceiptSummary.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'

const gridId = 'warehouse-documents-grid'
const mainSplitterId = `${gridId}-horizontal-splitter`
const secondarySplitterId = `${gridId}-vertical-splitter`
const { gridRef, focusGrid } = dataGrid.useGridRef()
const { repaintRow } = dataGrid.useUpdateMasterDetail()
const { route, router } = dataGrid.useGridRouter()
const { onDocumentInitNewRowAsync, onDocumentEditorPreparing } = useDocumentIdentifiers()
const i18n = getI18n()
const registryStore = useRegistryStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const documentType = ref(null)
const selectedDocument = ref(null)
const selectedRows = ref([])
const isReadOnly = ref(false)
const editors = {}

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.warehouseDocumentsUrl,
  modelName: 'WarehouseDocument',
  gridRef,
})
const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    edit: e => !(e.data?.rows_count > 0) && !entityLock.isLocked(false, e.entityId),
    delete: e => !(e.data?.rows_count > 0) && !entityLock.isLocked(false, e.entityId),
    view: e => e.data?.rows_count > 0 || entityLock.isLocked(false, e.entityId),
  },
})

const columns = useColumns(documentType, editors)

const editing = dataGrid.useDefaultEditing(useDialogTitle().dialogTitle, {
  colCount: 4,
  customizeItem: (item) => {
    const isTransfer = documentType.value === portalStore.documentTypesEnum.WAREHOUSE_TRANSFER
    if (item.dataField === 'destination_deposit') item.visible = isTransfer
    if (item.dataField === 'deposit') item.label = { text: i18n.t(isTransfer ? 'warehouseDocument.sourceDeposit' : 'common.deposit') }
    if (item.name === 'subject') item.visible = !isTransfer
  },
  items: [
    {
      itemType: 'group', caption: i18n.t('warehouseDocument.data'), colCount: 4,
      items: [
        { dataField: 'date', colSpan: 2 },
        { dataField: 'series' },
        { dataField: 'number_mnemonic' },
        ...['document_type', 'portal_warehouse_reason', 'deposit', 'destination_deposit', 'created_by', 'receipt_channel', 'status']
          .map(dataField => ({ dataField, colSpan: 4 })),
      ],
    },
    {
      itemType: 'group', colSpan: 3,
      items: [
        { itemType: 'group', name: 'subject', caption: i18n.t('common.subject'), items: ['subject_ref'] },
        {
          itemType: 'group', caption: i18n.t('warehouseDocument.reference'), colCount: 2,
          items: ['reference_number', 'reference_date'],
        },
        {
          itemType: 'group', caption: i18n.t('common.note', 2),
          items: [{ dataField: 'notes', editorType: 'dxTextArea', editorOptions: { height: '12rem' } }],
        },
      ],
    },
  ],
}, {
  width: '90vw',
  entityLock,
  onInitializedCustom: (e) => {
    if (isReadOnly.value) {
      const toolbarItems = e.component.option('toolbarItems') || []
      e.component.option('toolbarItems', toolbarItems.map((item, index) => index === toolbarItems.length - 1 ? item : { ...item, visible: false }))
    } else {
      entityLock.readOnlyToolbarItems(e)
    }
  },
})

const onToolbarPreparing = (e) => {
  dataGrid.useDefaultToolbar(e, { allowBatchDelete: true })
  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const batchDeleteAsync = async(selectedIds) => {
  await registryStore.api.batchDeleteWarehouseDocumentsAsync(selectedIds)
  selectedDocument.value = null
}

const onSplitterResize = (e, splitterId) => {
  const totalSize = e.component.option('orientation') === 'vertical' ? e.component.element().offsetHeight : e.component.element().offsetWidth
  const sizes = e.component.option('items').map(item => ({ size: ((parseFloat(item.size) / totalSize) * 100).toFixed(2) + '%' }))
  webSiteStore.setSplitterState(splitterId, sizes)
  gridRef.value?.instance.updateDimensions()
}

const restoreSplitterSize = (e, splitterId) => {
  const saved = webSiteStore.splitterStates[splitterId]
  if (saved) {
    try {
      saved.forEach((item, index) => {
        e.component.option(`items[${index}].size`, item.size)
      })
    } catch (err) {
      console.warn(`Splitter "${splitterId}": data error.`)
    }
  }
}

const onInitNewRow = (e) => {
  isReadOnly.value = false
  e.data.document_type = documentType.value = portalStore.documentTypesEnum.WAREHOUSE_LOAD
  e.data.date = new Date()
  e.data.receipt_channel = portalStore.warehouseReceiptChannelsEnum.MANUAL
  e.data.status = portalStore.documentStatusesEnum.OPEN
  e.promise = (async() => { await onDocumentInitNewRowAsync(e, registryStore.api.getWarehouseDocumentNextIdByDataAsync) })()
}

const onEditingStart = (e) => {
  isReadOnly.value = e.data?.rows_count > 0
  documentType.value = e.data.document_type
}

const onEditorPreparing = (e) => {
  onDocumentEditorPreparing(e, editors, registryStore.api.getWarehouseDocumentNextIdByDataAsync, gridRef)
  if (e.parentType === 'dataRow' && e.row.data?.rows_count > 0) {
    e.editorOptions.readOnly = true
  }
  if (e.parentType === 'dataRow' && ['date', 'document_type'].includes(e.dataField)) {
    e.editorOptions.readOnly = !!e.row.data?.id
  }
}

const onDetailRowSaved = async(parentId) => {
  const updatedDocument = await repaintRow(gridRef, registryStore.api.getWarehouseDocumentByIdAsync, parentId, parentId)
  if (updatedDocument && selectedDocument.value?.id === parentId) {
    selectedDocument.value = { ...selectedDocument.value, ...updatedDocument }
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.warehouseDocumentsUrl),
  columns: columns,
  editing: editing,
  repaintChangesOnly: false,
  entityLock,
  selection: dataGrid.useDefaultGridSelection(),
  onInitNewRow: onInitNewRow,
  onEditingStart: onEditingStart,
  onEditorPreparing: onEditorPreparing,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: e => { selectedRows.value = e.selectedRowsData },
  onFocusedRowChangedDebounced: e => { selectedDocument.value = e.row?.data || null },
  onClearFilter: () => router.replace(),
  focusGrid: focusGrid,
  splittedGrid: true,
  routerFilter: {
    fields: [{ series: 'series' }, { number: 'number_mnemonic' }, { document_type: 'document_type' }],
    getRoute: () => route,
    router: router,
  },
})
</script>

<template>
  <dx-splitter
    orientation="horizontal"
    height="90vh"
    @resize-end="e => onSplitterResize(e, mainSplitterId)"
    @item-collapsed="e => onSplitterResize(e, mainSplitterId)"
    @initialized="e => restoreSplitterSize(e, mainSplitterId)"
  >
    <dx-item
      :collapsible="true"
      min-size="50%"
    >
      <dx-splitter
        orientation="vertical"
        height="100%"
        @resize-end="e => onSplitterResize(e, secondarySplitterId)"
        @item-collapsed="e => onSplitterResize(e, secondarySplitterId)"
        @initialized="e => restoreSplitterSize(e, secondarySplitterId)"
      >
        <dx-item
          min-size="21%"
          :collapsible="true"
          collapsed-size="21%"
        >
          <dx-data-grid
            ref="gridRef"
            v-bind="gridOptions"
            class="q-pb-lg"
          >
            <template #customToolbarAddButtonTemplate>
              <custom-toolbar-add-button-template
                :grid="gridRef"
                :entity-lock="entityLock"
              />
            </template>
            <template #customToolbarDeleteButtonTemplate>
              <custom-toolbar-delete-button-template
                :disabled="selectedRows.some(row => row.rows_count > 0)"
                :selected-rows-id="selectedRows.map(row => row.id)"
                :grid="gridRef"
                :entity-lock="entityLock"
                :on-delete="batchDeleteAsync"
              />
            </template>
            <template
              #editButtonsTemplate="{ data: cellInfo }"
            >
              <edit-lock-buttons-template
                :entity-name="'WarehouseDocument'"
                :cell-info="cellInfo"
                :grid="gridRef"
                :entity-lock="entityLock"
                :is-locked-available="true"
                :buttons="rowButtons"
              >
                <template #after-actions>
                  <q-icon
                    v-if="cellInfo.data.rows_count > 0"
                    name="mdi-check-circle-outline"
                    :color="cellInfo.data.rows_count > 0 && cellInfo.data.confirmed_rows_count === cellInfo.data.rows_count ? 'positive' : 'grey'"
                    size="16px"
                    class="q-mx-xs"
                    role="img"
                    :aria-label="i18n.t(cellInfo.data.rows_count > 0 && cellInfo.data.confirmed_rows_count === cellInfo.data.rows_count ? 'warehouseDocument.confirmation.allConfirmed' : 'warehouseDocument.confirmation.pending')"
                  >
                    <q-tooltip>{{ i18n.t('warehouseDocument.confirmation.progress', { confirmed: cellInfo.data.confirmed_rows_count || 0, total: cellInfo.data.rows_count || 0 }) }}</q-tooltip>
                  </q-icon>
                </template>
              </edit-lock-buttons-template>
            </template>
            <template #gridLayoutConfigTemplate>
              <grid-layout-config-template
                :grid-ext-ref="gridRef"
                :grid-ext-id="gridId"
              />
            </template>
            <template #depositEditor="{ data }">
              <div>
                <deposit-drop-down-box
                  :key="`${data.column.dataField}-${data.value}`"
                  :data="data"
                  :disabled="data.data?.rows_count > 0"
                />
              </div>
            </template>
            <template #subjectEditor="{ data }">
              <subject-drop-down-box
                :data="data"
                :disabled="data.data?.rows_count > 0"
                show-clear-button
              />
            </template>
          </dx-data-grid>
        </dx-item>
        <dx-item
          min-size="21%"
          :collapsible="true"
          collapsed-size="21%"
        >
          <dx-tab-panel
            height="100%"
            :defer-rendering="false"
          >
            <dx-tab-item :title="i18n.t('common.row', 2)">
              <div class="full-height full-width">
                <warehouse-document-row-grid
                  v-if="selectedDocument"
                  :key="selectedDocument.id"
                  :data="selectedDocument"
                  :document-type="selectedDocument?.document_type"
                  :entity-lock="entityLock"
                  @row-saved="onDetailRowSaved"
                />
              </div>
            </dx-tab-item>
          </dx-tab-panel>
        </dx-item>
      </dx-splitter>
    </dx-item>
    <dx-item
      :collapsible="true"
      size="20%"
    >
      <div class="q-pl-lg">
        <warehouse-receipt-summary :data="selectedDocument" />
      </div>
    </dx-item>
  </dx-splitter>
</template>
